import React, { useMemo, useState } from 'react';
import { Box, Typography } from '@mui/material';
import OntologyBrowser, { BrowserSource, OntologyColumn } from '../Ontology/OntologyBrowser';
import { TierPill } from '../Benchmark/BenchmarkPrimitives';
import { CaseRow, CaseWorkflowQuery, caseWorkflowService } from '../../services/caseWorkflowService';
import { percent, transitionLabel } from './caseWorkflowVocabulary';
import { dsVar, ink } from '../../theme/tokens';

const PAGE_SIZE = 15;

interface CaseListTableProps {
    /** The parent aggregate's filters, passed through untouched. */
    query: CaseWorkflowQuery;
    /**
     * The page's query string, so the table's paging survives a reload.
     *
     * OntologyBrowser reads `page` from this prop but WRITES it to the URL, so
     * a table handed a literal {} turns its pages in the address bar and never
     * anywhere else.
     */
    urlFilters: Record<string, string>;
    onRowSelect: (row: CaseRow) => void;
    selectedKey?: string | null;
    coverage?: number | null;
}

interface Totals { records: number; cases: number | null; }

/**
 * The cases behind a number.
 *
 * Built on OntologyBrowser so it inherits the app's paging, URL-backed filters,
 * dim-in-place refresh and error copy rather than maintaining a second table.
 *
 * The filters it sends are the parent aggregate's own, handed down rather than
 * rebuilt — which is what makes a count of 15 open a list of 15.
 */
const CaseListTable: React.FC<CaseListTableProps> = ({ query, urlFilters, onRowSelect, selectedKey, coverage }) => {
    const [totals, setTotals] = useState<Totals | null>(null);

    const source: BrowserSource = useMemo(() => ({
        list: (params) => caseWorkflowService.getCases({
            ...query,
            page: Number(params.page) || 1,
            page_size: PAGE_SIZE
        }),
        normalize: (res) => {
            setTotals({
                records: res?.pagination?.totalRecords ?? 0,
                cases: res?.totalCases ?? null
            });
            return {
                documents: (res?.cases ?? []) as Record<string, any>[],
                pagination: {
                    page: res?.pagination?.currentPage ?? 1,
                    limit: res?.pagination?.pageSize ?? PAGE_SIZE,
                    total: res?.pagination?.totalRecords ?? 0,
                    pages: res?.pagination?.totalPages ?? 1
                }
            };
        },
        // Keyed on the record path, because case_id is an optional contract
        // column: an org with customer-managed records carries none at all, and
        // a list keyed on it would come back empty while the counts showed work.
        codeOf: (doc) => `${doc.recordType}:${doc.recordKey}`,
        detailBase: '',
        label: 'cases'
    }), [query]);

    const columns: OntologyColumn[] = useMemo(() => [
        {
            key: 'caseId',
            label: 'Case',
            width: '22%',
            render: (doc) => (
                <Box>
                    <Typography sx={{ fontSize: '0.8125rem', color: dsVar('fg-body') }}>
                        {doc.caseId || doc.recordKey}
                    </Typography>
                    <Typography sx={{ fontSize: '0.6875rem', color: dsVar('fg-muted') }}>
                        {doc.customerName || doc.customerId || doc.recordType}
                    </Typography>
                </Box>
            )
        },
        {
            key: 'priority',
            label: 'Priority',
            width: '14%',
            render: (doc) => (doc.priority
                ? <TierPill code={doc.priority} />
                : <Typography sx={{ fontSize: '0.75rem', color: dsVar('fg-muted') }}>Not yet scored</Typography>)
        },
        {
            key: 'alertTypes',
            label: 'Detection signals',
            width: '24%',
            render: (doc) => (
                <Typography sx={{ fontSize: '0.75rem', color: dsVar('fg-2'), textTransform: 'capitalize' }}>
                    {(doc.alertTypes ?? []).length
                        ? (doc.alertTypes as string[]).map((t) => t.replace(/_/g, ' ')).join(', ')
                        : '—'}
                </Typography>
            )
        },
        {
            key: 'workflowState',
            label: 'State now',
            width: '18%',
            render: (doc) => (
                <Typography sx={{ fontSize: '0.75rem', color: dsVar('fg-soft') }}>
                    {String(doc.workflowState || '—').replace(/_/g, ' ')}
                </Typography>
            )
        },
        {
            key: 'transitionCode',
            label: 'Matched on',
            width: '22%',
            render: (doc) => (
                <Typography sx={{ fontSize: '0.75rem', color: dsVar('fg-2') }}>
                    {transitionLabel(doc.transitionCode)}
                </Typography>
            )
        }
    ], []);

    return (
        <Box>
            {/*
              * Reported here as well as on the funnel: a list that silently
              * thins reads as data loss rather than as a missing optional column.
              */}
            {/*
              * Both grains, stated. A card reading "39 cases" opens 1,746 alert
              * records here — correctly, since one case aggregates every alert
              * raised against that customer. Unstated, it reads as the list
              * contradicting the number that opened it.
              */}
            {totals && (
                <Typography sx={{ fontSize: '0.75rem', color: dsVar('fg-2'), mb: 1.5 }}>
                    {totals.records.toLocaleString()} {totals.records === 1 ? 'record' : 'records'}
                    {totals.cases !== null && totals.cases > 0 && (
                        <> across {totals.cases.toLocaleString()} {totals.cases === 1 ? 'case' : 'cases'}</>
                    )}
                </Typography>
            )}

            {coverage !== null && coverage !== undefined && coverage < 1 && (
                <Typography sx={{ fontSize: '0.6875rem', color: ink('#FBBF24'), mb: 1.5 }}>
                    {percent(coverage)} of these records carry a case id. The rest are listed by their record key.
                </Typography>
            )}
            <OntologyBrowser
                source={source}
                columns={columns}
                // Narrowed by the dashboard's own filter bar — the same
                // predicate the aggregate above it used — so the table offers
                // no controls of its own that could disagree with the number
                // that was clicked. It still needs the query string for `page`.
                filters={urlFilters}
                filterControls={[]}
                // No search field: /cases has no free-text parameter, and the
                // browser would send `q`, have it ignored, and read to the user
                // as a search that simply never matches.
                showActivityFilter={false}
                emptyMessage="No cases match this selection."
                onRowSelect={(doc: any) => onRowSelect(doc as CaseRow)}
                selectedCode={selectedKey ?? undefined}
            />
        </Box>
    );
};

export default CaseListTable;
